import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters");
    if (password !== confirm) return setError("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset — please log in with your new password");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center p-6">
      <div className="w-full max-w-md" data-testid="reset-password-page">
        <Link to="/login" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400 hover:text-amber-500 mb-6">
          <ArrowLeft className="w-3 h-3" /> Back to log in
        </Link>
        <div className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">Reset</div>
        <h1 className="display text-4xl mt-2">New Password</h1>

        {!token && (
          <div className="mt-6 border border-red-800 bg-red-950/40 text-red-300 px-4 py-3 text-sm">Missing reset token. Please request a new link.</div>
        )}

        {token && (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-password-form">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 block mb-2">New Password</label>
              <input
                data-testid="reset-password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none px-4 py-3"
                placeholder="••••••••"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 block mb-2">Confirm Password</label>
              <input
                data-testid="reset-confirm-input"
                type="password"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none px-4 py-3"
                placeholder="••••••••"
              />
            </div>
            {error && <div className="border border-red-800 bg-red-950/40 text-red-300 px-4 py-3 text-sm">{error}</div>}
            <button data-testid="reset-submit-btn" disabled={loading} className="w-full bg-amber-500 text-neutral-950 font-bold uppercase tracking-widest py-3 hover:bg-amber-400 transition-colors disabled:opacity-60">
              {loading ? "Saving…" : "Set new password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
